import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Car, Phone, Mail, MapPin, Pencil, Trash2, FileText, Clock, Wrench, User, CheckCircle2, MessageSquare, Upload, Download, X, CalendarClock, Filter, Paperclip, RotateCcw, Package, CircleDot, Printer, Camera, Loader2, QrCode, Star, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { TireStorageBanner } from '@/components/client/TireStorageBanner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import ClientCommunicationTab from '@/components/client/ClientCommunicationTab';
import ClientAttachmentsTab from '@/components/client/ClientAttachmentsTab';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';
import { ClientHistorySection } from '@/components/client/ClientHistorySection';

const EMPTY_VFORM = { brand: '', model: '', type: '', plate: '', mileage: 0, vin: '', chassis_number: '', color: '', seats_total: 0, seats_front: 0, registration_number: '', type_approval: '', displacement_cc: 0, power_kw: 0, first_registration: '', last_inspection: '', empty_weight_kg: 0, payload_kg: 0, total_weight_kg: 0, emission_code: '', vehicle_genre: '', vehicle_genre_code: '', body_type: '', body_code: '', special_usage: '' };

const STATUS_CONFIG = {
  open: { label: 'Ouverte', color: 'bg-blue-100 text-blue-700' },
  signed: { label: 'Signee', color: 'bg-emerald-100 text-emerald-700' },
  in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Terminée', color: 'bg-slate-100 text-slate-700' },
};

const CATEGORY_COLORS = {
  mecanique: 'bg-blue-500',
  carrosserie: 'bg-amber-500',
  assurance: 'bg-violet-500',
};

const INTERACTION_ICONS = {
  contact_telephonique: Phone,
  contact_physique: User,
  promotion_atelier: Wrench,
  cas_carrosserie: Car,
  vente_voiture: Car,
  proposition_offre: FileText,
  rapport_atelier: Wrench,
};

const INTERACTION_COLORS = {
  contact_telephonique: 'bg-sky-100 text-sky-700 border-sky-200',
  contact_physique: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  promotion_atelier: 'bg-amber-100 text-amber-700 border-amber-200',
  cas_carrosserie: 'bg-orange-100 text-orange-700 border-orange-200',
  vente_voiture: 'bg-violet-100 text-violet-700 border-violet-200',
  proposition_offre: 'bg-rose-100 text-rose-700 border-rose-200',
  rapport_atelier: 'bg-emerald-100 text-emerald-700 border-emerald-300',
};

const TIRE_CONDITION_COLORS = { bon: 'bg-emerald-100 text-emerald-700', moyen: 'bg-amber-100 text-amber-700', a_remplacer: 'bg-red-100 text-red-700' };

const ClientDetailPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [interactionTypes, setInteractionTypes] = useState({ types: [], labels: {} });
  const [filterType, setFilterType] = useState('all');
  const [storages, setStorages] = useState([]);
  const [activeTab, setActiveTab] = useState('historique');
  const [showVehicleDialog, setShowVehicleDialog] = useState(false);
  const [showInteractionDialog, setShowInteractionDialog] = useState(false);
  const [showStorageDialog, setShowStorageDialog] = useState(false);
  const [showRotateDialog, setShowRotateDialog] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [editInteraction, setEditInteraction] = useState(null);
  const [editStorage, setEditStorage] = useState(null);
  const [rotateStorage, setRotateStorage] = useState(null);
  const [vForm, setVForm] = useState({ ...EMPTY_VFORM });
  const [scanning, setScanning] = useState(false);
  const [qrSession, setQrSession] = useState(null);
  const pollRef = useRef(null);
  const pollDoneRef = useRef(false);
  const [iForm, setIForm] = useState({ interaction_type: '', notes: '', follow_up_needed: false, follow_up_date: '', follow_up_action: '', created_by: '' });
  const [sForm, setSForm] = useState({ vehicle_id: '', tire_type: 'roues_completes', rim_type: 'alu', tire_brand: '', tire_dimension: '', qty_bon: 4, qty_moyen: 0, qty_a_remplacer: 0, season: 'hiver', storage_location: '', start_date: '', end_date: '', notes: '' });
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [satisfactionRatings, setSatisfactionRatings] = useState([]);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { loadClient(); loadInterventions(); loadInteractions(); loadInteractionTypes(); loadStorages(); }, [clientId]);
  useEffect(() => { if (client?.email) loadSatisfaction(client.email); }, [client?.email]);

  const loadClient = async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}`);
      setClient(res.data);
      setVehicles(res.data.vehicles || []);
    } catch { toast.error('Client non trouve'); navigate('/admin/clients'); }
  };

  const loadInterventions = async () => {
    try { const res = await axios.get(`${API}/clients/${clientId}/interventions`); setInterventions(res.data); } catch (err) { console.error(err); }
  };

  const loadInteractions = async () => {
    try {
      const params = filterType !== 'all' ? { interaction_type: filterType } : {};
      const res = await axios.get(`${API}/clients/${clientId}/crm`, { params });
      setInteractions(res.data);
    } catch (err) { console.error(err); }
  };

  const loadInteractionTypes = async () => {
    try { const res = await axios.get(`${API}/crm/interaction-types`); setInteractionTypes(res.data); } catch (err) { console.error(err); }
  };

  const loadStorages = async () => {
    try { const res = await axios.get(`${API}/clients/${clientId}/gardiennage`); setStorages(res.data); } catch (err) { console.error(err); }
  };

  const loadSatisfaction = async (email) => {
    try {
      // Load by email and by client_id to catch all ratings
      const [byEmail, byId] = await Promise.all([
        email ? axios.get(`${API}/client-notifications/satisfaction-ratings/${email}`).catch(() => ({data:[]})) : {data:[]},
        axios.get(`${API}/client-notifications/satisfaction-ratings/${clientId}`).catch(() => ({data:[]})),
      ]);
      const all = [...byEmail.data, ...byId.data];
      const unique = all.filter((r, i, arr) => arr.findIndex(x => x.id === r.id) === i);
      unique.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      setSatisfactionRatings(unique);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { loadInteractions(); }, [filterType]);

  // Vehicle handlers
  const handleVehicleSubmit = async (e) => {
    e.preventDefault();
    if (!vForm.brand || !vForm.plate) { toast.error('Marque et plaque requises'); return; }
    try {
      if (editVehicle) {
        await axios.put(`${API}/vehicles/${editVehicle.id}`, { ...vForm, client_id: clientId });
        toast.success('Véhicule modifié');
      } else {
        await axios.post(`${API}/vehicles`, { ...vForm, client_id: clientId });
        toast.success('Véhicule ajouté');
      }
      setShowVehicleDialog(false); setEditVehicle(null);
      setVForm({ ...EMPTY_VFORM });
      loadClient();
    } catch { toast.error('Erreur'); }
  };

  const deleteVehicle = async (vehicleId) => {
    if (!window.confirm('Supprimer ce véhicule ?')) return;
    try { await axios.delete(`${API}/vehicles/${vehicleId}`); toast.success('Vehicule supprime'); loadClient(); } catch { toast.error('Erreur'); }
  };

  const openEditVehicle = (v) => {
    setEditVehicle(v);
    setVForm({
      brand: v.brand, model: v.model, type: v.type || '', plate: v.plate, mileage: v.mileage || 0, vin: v.vin || '',
      chassis_number: v.chassis_number || '', color: v.color || '', seats_total: v.seats_total || 0, seats_front: v.seats_front || 0,
      registration_number: v.registration_number || '', type_approval: v.type_approval || '', displacement_cc: v.displacement_cc || 0,
      power_kw: v.power_kw || 0, first_registration: v.first_registration || '', last_inspection: v.last_inspection || '',
      empty_weight_kg: v.empty_weight_kg || 0, payload_kg: v.payload_kg || 0, total_weight_kg: v.total_weight_kg || 0,
      emission_code: v.emission_code || '', vehicle_genre: v.vehicle_genre || '', vehicle_genre_code: v.vehicle_genre_code || '',
      body_type: v.body_type || '', body_code: v.body_code || '', special_usage: v.special_usage || ''
    });
    setShowVehicleDialog(true);
  };

  const handleScanCarteGrise = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/vehicles/scan-carte-grise`, formData);
      const d = res.data;
      setVForm(prev => ({
        ...prev,
        plate: d.plate || prev.plate,
        brand: d.brand || prev.brand,
        model: d.model || prev.model,
        chassis_number: d.chassis_number || prev.chassis_number,
        vin: d.chassis_number || prev.vin,
        color: d.color || prev.color,
        seats_total: d.seats_total || prev.seats_total,
        seats_front: d.seats_front || prev.seats_front,
        registration_number: d.registration_number || prev.registration_number,
        type_approval: d.type_approval || prev.type_approval,
        displacement_cc: d.displacement_cc || prev.displacement_cc,
        power_kw: d.power_kw || prev.power_kw,
        first_registration: d.first_registration || prev.first_registration,
        last_inspection: d.last_inspection || prev.last_inspection,
        empty_weight_kg: d.empty_weight_kg || prev.empty_weight_kg,
        payload_kg: d.payload_kg || prev.payload_kg,
        total_weight_kg: d.total_weight_kg || prev.total_weight_kg,
        emission_code: d.emission_code || prev.emission_code,
        vehicle_genre: d.vehicle_genre || prev.vehicle_genre,
        vehicle_genre_code: d.vehicle_genre_code || prev.vehicle_genre_code,
        body_type: d.body_type || prev.body_type,
        body_code: d.body_code || prev.body_code,
        special_usage: d.special_usage || prev.special_usage,
      }));
      toast.success('Carte grise scannée avec succès !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du scan');
    } finally {
      setScanning(false);
      e.target.value = '';
    }
  };

  const startQrScan = async () => {
    try {
      const res = await axios.post(`${API}/scan-sessions`);
      setQrSession(res.data);
      pollDoneRef.current = false;
      // Start polling
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        if (pollDoneRef.current) return;
        try {
          const poll = await axios.get(`${API}/scan-sessions/${res.data.id}`);
          if (pollDoneRef.current) return;
          if (poll.data.status === 'completed' && poll.data.result) {
            pollDoneRef.current = true;
            clearInterval(pollRef.current);
            pollRef.current = null;
            const d = poll.data.result;
            setVForm(prev => ({
              ...prev,
              plate: d.plate || prev.plate, brand: d.brand || prev.brand, model: d.model || prev.model,
              chassis_number: d.chassis_number || prev.chassis_number, vin: d.chassis_number || prev.vin,
              color: d.color || prev.color, seats_total: d.seats_total || prev.seats_total,
              seats_front: d.seats_front || prev.seats_front, registration_number: d.registration_number || prev.registration_number,
              type_approval: d.type_approval || prev.type_approval, displacement_cc: d.displacement_cc || prev.displacement_cc,
              power_kw: d.power_kw || prev.power_kw, first_registration: d.first_registration || prev.first_registration,
              last_inspection: d.last_inspection || prev.last_inspection, empty_weight_kg: d.empty_weight_kg || prev.empty_weight_kg,
              payload_kg: d.payload_kg || prev.payload_kg, total_weight_kg: d.total_weight_kg || prev.total_weight_kg,
              emission_code: d.emission_code || prev.emission_code, vehicle_genre: d.vehicle_genre || prev.vehicle_genre,
              vehicle_genre_code: d.vehicle_genre_code || prev.vehicle_genre_code, body_type: d.body_type || prev.body_type,
              body_code: d.body_code || prev.body_code, special_usage: d.special_usage || prev.special_usage,
            }));
            setQrSession(null);
            toast.success('Carte grise scannée via téléphone !');
          } else if (poll.data.status === 'error') {
            pollDoneRef.current = true;
            clearInterval(pollRef.current);
            pollRef.current = null;
            setQrSession(null);
            toast.error(poll.data.error || 'Erreur lors du scan');
          }
        } catch { /* ignore poll errors */ }
      }, 2000);
    } catch (err) {
      toast.error('Erreur creation session QR');
    }
  };

  const stopQrScan = () => {
    pollDoneRef.current = true;
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setQrSession(null);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // CRM Interaction handlers
  const openNewInteraction = () => {
    setEditInteraction(null);
    const adminName = auth?.admin ? `${auth.admin.first_name} ${auth.admin.last_name}` : '';
    const adminId = auth?.admin?.id || '';
    setIForm({ interaction_type: '', notes: '', follow_up_needed: false, follow_up_date: '', follow_up_action: '', created_by: adminName, created_by_id: adminId });
    setPendingFiles([]); setShowInteractionDialog(true);
  };

  const openEditInteraction = (inter) => {
    setEditInteraction(inter);
    setIForm({ interaction_type: inter.interaction_type, notes: inter.notes || '', follow_up_needed: inter.follow_up_needed || false, follow_up_date: inter.follow_up_date || '', follow_up_action: inter.follow_up_action || '', created_by: inter.created_by || '' });
    setPendingFiles([]); setShowInteractionDialog(true);
  };

  const handleInteractionSubmit = async (e) => {
    e.preventDefault();
    if (!iForm.interaction_type) { toast.error('Sélectionnez un type de contact'); return; }
    try {
      let interactionId;
      if (editInteraction) {
        await axios.put(`${API}/crm/${editInteraction.id}`, { interaction_type: iForm.interaction_type, notes: iForm.notes, follow_up_needed: iForm.follow_up_needed, follow_up_date: iForm.follow_up_date || null, follow_up_action: iForm.follow_up_action });
        interactionId = editInteraction.id;
        toast.success('Contact modifié');
      } else {
        const res = await axios.post(`${API}/clients/${clientId}/crm`, iForm);
        interactionId = res.data.id;
        toast.success('Contact enregistre');
      }
      if (pendingFiles.length > 0 && interactionId) {
        setUploadingFiles(true);
        for (const file of pendingFiles) {
          const formData = new FormData(); formData.append('file', file);
          try { await axios.post(`${API}/crm/${interactionId}/files`, formData); } catch { toast.error(`Erreur upload: ${file.name}`); }
        }
        setUploadingFiles(false);
      }
      setShowInteractionDialog(false); setEditInteraction(null); setPendingFiles([]); loadInteractions();
    } catch { toast.error('Erreur'); }
  };

  const deleteInteraction = async (interactionId) => {
    if (!window.confirm('Supprimer cette fiche de contact ?')) return;
    try { await axios.delete(`${API}/crm/${interactionId}`); toast.success('Contact supprime'); loadInteractions(); } catch { toast.error('Erreur'); }
  };

  const deleteFile = async (fileId) => {
    if (!window.confirm('Supprimer ce fichier ?')) return;
    try { await axios.delete(`${API}/crm/files/${fileId}`); toast.success('Fichier supprime'); loadInteractions(); } catch { toast.error('Erreur'); }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f => { if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} depasse 10 MB`); return false; } return true; });
    setPendingFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingFile = (idx) => setPendingFiles(prev => prev.filter((_, i) => i !== idx));

  const handleFileUploadForExisting = async (interactionId, e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} depasse 10 MB`); continue; }
      const formData = new FormData(); formData.append('file', file);
      try { await axios.post(`${API}/crm/${interactionId}/files`, formData); toast.success(`${file.name} ajoute`); } catch { toast.error(`Erreur upload: ${file.name}`); }
    }
    loadInteractions();
  };

  // Gardiennage handlers
  const resetSForm = () => setSForm({ vehicle_id: '', tire_type: 'roues_completes', rim_type: 'alu', tire_brand: '', tire_dimension: '', qty_bon: 4, qty_moyen: 0, qty_a_remplacer: 0, season: 'hiver', storage_location: '', start_date: '', end_date: '', notes: '', accessories: [] });

  const openNewStorage = () => { setEditStorage(null); resetSForm(); setShowStorageDialog(true); };

  const openEditStorage = (s) => {
    setEditStorage(s);
    setSForm({ vehicle_id: s.vehicle_id, tire_type: s.tire_type, rim_type: s.rim_type, tire_brand: s.tire_brand, tire_dimension: s.tire_dimension, qty_bon: s.qty_bon || 0, qty_moyen: s.qty_moyen || 0, qty_a_remplacer: s.qty_a_remplacer || 0, season: s.season, storage_location: s.storage_location, start_date: s.start_date, end_date: s.end_date, notes: s.notes, accessories: s.accessories || [] });
    setShowStorageDialog(true);
  };

  const openRotateStorage = (s) => {
    setRotateStorage(s);
    const newSeason = s.season === 'hiver' ? 'ete' : 'hiver';
    const today = new Date();
    const startDate = today.toISOString().split('T')[0];
    const endDate = new Date(today.setMonth(today.getMonth() + 6)).toISOString().split('T')[0];
    setSForm({ vehicle_id: s.vehicle_id, tire_type: s.tire_type, rim_type: s.rim_type, tire_brand: s.tire_brand, tire_dimension: s.tire_dimension, qty_bon: s.qty_bon || 0, qty_moyen: s.qty_moyen || 0, qty_a_remplacer: s.qty_a_remplacer || 0, season: newSeason, storage_location: s.storage_location, start_date: startDate, end_date: endDate, notes: '', accessories: s.accessories || [] });
    setShowRotateDialog(true);
  };

  const handleStorageSubmit = async (e) => {
    e.preventDefault();
    if (!sForm.vehicle_id) { toast.error('Sélectionnez un véhicule'); return; }
    try {
      if (editStorage) {
        await axios.put(`${API}/gardiennage/${editStorage.id}`, sForm);
        toast.success('Gardiennage modifié');
      } else {
        await axios.post(`${API}/clients/${clientId}/gardiennage`, sForm);
        toast.success('Gardiennage créé');
      }
      setShowStorageDialog(false); setEditStorage(null); resetSForm(); loadStorages();
    } catch { toast.error('Erreur'); }
  };

  const handleRotateSubmit = async (e) => {
    e.preventDefault();
    if (!rotateStorage) return;
    try {
      await axios.post(`${API}/gardiennage/${rotateStorage.id}/rotate`, sForm);
      toast.success('Rotation saisonniere effectuee');
      setShowRotateDialog(false); setRotateStorage(null); resetSForm(); loadStorages();
    } catch { toast.error('Erreur'); }
  };

  const returnStorage = async (storageId) => {
    if (!window.confirm('Marquer comme rendu au client ? Le gardiennage sera terminé.')) return;
    try { await axios.put(`${API}/gardiennage/${storageId}/return`); toast.success('Marque comme rendu'); loadStorages(); } catch { toast.error('Erreur'); }
  };

  const mountStorage = async (storageId) => {
    if (!window.confirm('Marquer comme monte sur le vehicule ?')) return;
    try { await axios.put(`${API}/gardiennage/${storageId}/mount`); toast.success('Pneus marques comme montes sur le vehicule'); loadStorages(); } catch { toast.error('Erreur'); }
  };

  const unmountStorage = async (storageId) => {
    if (!window.confirm('Remettre les pneus en stock ?')) return;
    try { await axios.put(`${API}/gardiennage/${storageId}/unmount`); toast.success('Pneus remis en stock'); loadStorages(); } catch { toast.error('Erreur'); }
  };

  const deleteStorage = async (storageId) => {
    if (!window.confirm('Supprimer ce gardiennage ?')) return;
    try { await axios.delete(`${API}/gardiennage/${storageId}`); toast.success('Gardiennage supprime'); loadStorages(); } catch { toast.error('Erreur'); }
  };

  // Utility
  const formatDate = (isoStr) => { if (!isoStr) return ''; try { return new Date(isoStr).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return isoStr; } };
  const formatDateTime = (isoStr) => { if (!isoStr) return ''; try { return new Date(isoStr).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return isoStr; } };
  const formatFileSize = (bytes) => { if (bytes < 1024) return `${bytes} B`; if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`; return `${(bytes / (1024 * 1024)).toFixed(1)} MB`; };

  const downloadPdf = async (section) => {
    try {
      const { downloadPdf: safePdf } = await import('@/utils/api');
      await safePdf(`${API}/clients/${clientId}/pdf/${section}`, `client_${clientId.substring(0, 8)}_${section}.pdf`);
    } catch (err) {
      console.error('PDF download error:', err);
      toast.error('Erreur lors du telechargement du PDF');
    }
  };

  const [editingClient, setEditingClient] = useState(false);
  const [editForm, setEditForm] = useState({});

  const startEditClient = () => {
    setEditForm({
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      phone: client.phone || '',
      email: client.email || '',
      address: client.address || '',
      postal_code: client.postal_code || '',
      city: client.city || '',
      country: client.country || 'Suisse',
      notes: client.notes || '',
    });
    setEditingClient(true);
  };

  const saveClient = async () => {
    try {
      await axios.put(`${API}/clients/${clientId}`, editForm);
      toast.success('Client modifie');
      setEditingClient(false);
      loadClient();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  if (!client) return <AdminLayout auth={auth} onLogout={onLogout}><div className="p-8 text-center text-slate-500">{t('common.loading')}</div></AdminLayout>;

  const activeStorages = storages.filter(s => s.status === 'en_stock');
  const mountedStorages = storages.filter(s => s.status === 'monte_sur_vehicule');
  const returnedStorages = storages.filter(s => s.status === 'rendu');

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-to-clients">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900">{client.first_name} {client.last_name}</h1>
            <p className="text-base text-slate-600 mt-1">Fiche client</p>
          </div>
          <div className="ml-auto">
            <Button variant="outline" size="sm" onClick={startEditClient} data-testid="edit-client-btn">
              <Pencil className="w-4 h-4 mr-1" /> Modifier
            </Button>
          </div>
        </div>

        {/* Edit Client Form (inline) */}
        {editingClient && (
          <Card className="p-5 rounded-xl border-2 border-blue-200 bg-blue-50/30 space-y-3" data-testid="edit-client-form">
            <h3 className="text-sm font-bold text-slate-900 mb-2">Modifier les informations</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Prenom</Label>
                <Input value={editForm.first_name} onChange={e => setEditForm(f => ({...f, first_name: e.target.value}))} className="h-8" data-testid="edit-first-name" />
              </div>
              <div>
                <Label className="text-xs">Nom</Label>
                <Input value={editForm.last_name} onChange={e => setEditForm(f => ({...f, last_name: e.target.value}))} className="h-8" data-testid="edit-last-name" />
              </div>
              <div>
                <Label className="text-xs">Telephone</Label>
                <Input value={editForm.phone} onChange={e => setEditForm(f => ({...f, phone: e.target.value}))} className="h-8" data-testid="edit-phone" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input value={editForm.email} onChange={e => setEditForm(f => ({...f, email: e.target.value}))} className="h-8" data-testid="edit-email" />
              </div>
              <div>
                <Label className="text-xs">Adresse</Label>
                <Input value={editForm.address} onChange={e => setEditForm(f => ({...f, address: e.target.value}))} className="h-8" data-testid="edit-address" />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs">NPA</Label>
                  <Input value={editForm.postal_code} onChange={e => setEditForm(f => ({...f, postal_code: e.target.value}))} className="h-8" data-testid="edit-postal" />
                </div>
                <div className="flex-1">
                  <Label className="text-xs">Ville</Label>
                  <Input value={editForm.city} onChange={e => setEditForm(f => ({...f, city: e.target.value}))} className="h-8" data-testid="edit-city" />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={editForm.notes} onChange={e => setEditForm(f => ({...f, notes: e.target.value}))} rows={2} data-testid="edit-notes" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setEditingClient(false)}>Annuler</Button>
              <Button size="sm" onClick={saveClient} className="bg-blue-600 hover:bg-blue-700" data-testid="save-client-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Enregistrer
              </Button>
            </div>
          </Card>
        )}

        {/* Client Info */}
        <Card className="p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {client.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /><span className="text-sm">{client.phone}</span></div>}
            {client.email && <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /><span className="text-sm">{client.email}</span></div>}
            {client.address && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" /><span className="text-sm">{client.address}</span></div>}
          </div>
          {client.notes && <p className="mt-3 text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">{client.notes}</p>}
        </Card>

        <TireStorageBanner storages={storages} onOpen={() => setActiveTab('gardiennage')} onNew={() => { setActiveTab('gardiennage'); openNewStorage(); }} />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full grid grid-cols-7 bg-slate-100/80 p-1 rounded-xl gap-1">
            <TabsTrigger value="historique" data-testid="tab-historique" className="rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs font-semibold gap-1.5 transition-all"><TrendingUp className="w-3.5 h-3.5" /> Suivi 360</TabsTrigger>
            <TabsTrigger value="crm" data-testid="tab-crm" className="rounded-lg data-[state=active]:bg-violet-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs font-semibold gap-1.5 transition-all"><MessageSquare className="w-3.5 h-3.5" /> CRM</TabsTrigger>
            <TabsTrigger value="communication" data-testid="tab-communication" className="rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs font-semibold gap-1.5 transition-all"><Phone className="w-3.5 h-3.5" /> Contact</TabsTrigger>
            <TabsTrigger value="pieces-jointes" data-testid="tab-pieces-jointes" className="rounded-lg data-[state=active]:bg-orange-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs font-semibold gap-1.5 transition-all"><Paperclip className="w-3.5 h-3.5" /> Documents</TabsTrigger>
            <TabsTrigger value="gardiennage" data-testid="tab-gardiennage" className="rounded-lg data-[state=active]:bg-amber-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs font-semibold gap-1.5 transition-all"><Package className="w-3.5 h-3.5" /> Pneus</TabsTrigger>
            <TabsTrigger value="vehicles" data-testid="tab-vehicles" className="rounded-lg data-[state=active]:bg-cyan-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs font-semibold gap-1.5 transition-all"><Car className="w-3.5 h-3.5" /> Vehicules</TabsTrigger>
            <TabsTrigger value="interventions" data-testid="tab-interventions" className="rounded-lg data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs font-semibold gap-1.5 transition-all"><FileText className="w-3.5 h-3.5" /> Interv.</TabsTrigger>
          </TabsList>

          {/* Historique 360 Tab */}
          <TabsContent value="historique">
            <ClientHistorySection clientId={clientId} />
          </TabsContent>

          {/* CRM Tab */}
          <TabsContent value="crm">
            <div className="space-y-4">
              {/* Satisfaction Ratings */}
              {satisfactionRatings.length > 0 && (
                <Card className="p-5 rounded-xl border border-slate-200 shadow-sm" data-testid="satisfaction-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Star className="w-5 h-5 text-amber-400" />
                    <h3 className="font-semibold text-slate-800 dark:text-white text-sm">Satisfaction client ({satisfactionRatings.length})</h3>
                    <div className="flex items-center gap-1 ml-2">
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} className={`w-4 h-4 ${s <= Math.round(satisfactionRatings.reduce((a,r) => a + (r.rating||0), 0) / satisfactionRatings.length) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                      ))}
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-300 ml-1">
                        {(satisfactionRatings.reduce((a,r) => a + (r.rating||0), 0) / satisfactionRatings.length).toFixed(1)}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {satisfactionRatings.map(r => (
                      <div key={r.id} className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3" data-testid={`rating-${r.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="flex shrink-0">
                                {[1,2,3,4,5].map(s => (
                                  <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                                ))}
                              </div>
                              <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{r.rating}/5</span>
                            </div>
                            {r.message && <p className="text-sm text-slate-700 dark:text-slate-300 mb-1">"{r.message}"</p>}
                            <div className="flex items-center gap-2 flex-wrap">
                              {r.vehicle && (
                                <span className="text-[10px] bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <Car className="w-3 h-3" /> {r.vehicle}
                                </span>
                              )}
                              {r.intervention && (
                                <span className="text-[10px] bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full">
                                  {r.intervention}
                                </span>
                              )}
                              {r.invoice_number && (
                                <span className="text-[10px] bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full">
                                  {r.invoice_number}
                                </span>
                              )}
                              <span className="text-[10px] text-slate-400">{formatDateTime(r.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Vehicle quick links */}
              {vehicles.length > 0 && (
                <Card className="p-4 rounded-xl border border-indigo-100 bg-indigo-50/30" data-testid="crm-vehicles-section">
                  <div className="flex items-center gap-2 mb-2">
                    <Car className="w-4 h-4 text-indigo-600" />
                    <span className="text-xs font-semibold text-indigo-700 uppercase">Vehicule{vehicles.length > 1 ? 's' : ''} du client</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {vehicles.map(v => (
                      <button
                        key={v.id}
                        onClick={() => { openEditVehicle(v); }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-indigo-200 rounded-lg text-sm text-indigo-800 hover:bg-indigo-100 hover:border-indigo-300 transition-colors"
                        data-testid={`crm-vehicle-link-${v.id}`}
                      >
                        <Car className="w-3.5 h-3.5" />
                        <span className="font-medium">{v.brand} {v.model}</span>
                        <span className="font-mono text-xs text-indigo-500">{v.plate}</span>
                        <Pencil className="w-3 h-3 text-indigo-400" />
                      </button>
                    ))}
                  </div>
                </Card>
              )}

              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-[220px]" data-testid="crm-filter-type"><SelectValue placeholder="Tous les types" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      {interactionTypes.types.map(t => <SelectItem key={t} value={t}>{interactionTypes.labels[t]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" onClick={openNewInteraction} data-testid="add-interaction-btn"><Plus className="mr-1 w-4 h-4" /> Nouveau contact</Button>
                <Button size="sm" variant="outline" onClick={() => downloadPdf('crm')} data-testid="pdf-crm-btn"><Printer className="mr-1 w-4 h-4" /> PDF</Button>
              </div>

              {interactions.length === 0 ? (
                <Card className="p-8 text-center rounded-xl">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm text-slate-500">Aucun contact enregistre</p>
                  <Button size="sm" variant="outline" className="mt-3" onClick={openNewInteraction} data-testid="add-first-interaction-btn"><Plus className="mr-1 w-4 h-4" /> Ajouter un premier contact</Button>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {interactions.map(inter => {
                    const Icon = INTERACTION_ICONS[inter.interaction_type] || MessageSquare;
                    const colorClass = INTERACTION_COLORS[inter.interaction_type] || 'bg-slate-100 text-slate-700 border-slate-200';
                    const typeLabel = interactionTypes.labels[inter.interaction_type] || inter.interaction_type;
                    return (
                      <Card key={inter.id} className="p-4 rounded-xl border border-slate-200 shadow-sm" data-testid={`interaction-card-${inter.id}`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${colorClass.split(' ').slice(0, 1).join('')}`}><Icon className="w-5 h-5" /></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className={`text-xs border ${colorClass}`}>{typeLabel}</Badge>
                              <span className="text-xs text-slate-400">{formatDateTime(inter.created_at)}</span>
                              {inter.created_by && <span className="text-xs text-slate-500">par {inter.created_by}</span>}
                            </div>
                            {inter.notes && <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{inter.notes}</p>}
                            {inter.follow_up_needed && (
                              <div className="flex items-center gap-2 mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                                <CalendarClock className="w-4 h-4 text-amber-600" />
                                <span className="text-xs text-amber-700 font-medium">Suivi: {inter.follow_up_date ? formatDate(inter.follow_up_date) : 'Non date'}{inter.follow_up_action && ` - ${inter.follow_up_action}`}</span>
                              </div>
                            )}
                            {inter.files && inter.files.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {inter.files.map(f => (
                                  <div key={f.id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5 group" data-testid={`file-${f.id}`}>
                                    <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                                    <span className="text-xs text-slate-600 truncate flex-1">{f.original_name}</span>
                                    <span className="text-xs text-slate-400">{formatFileSize(f.size)}</span>
                                    <a href={`${API}/crm/files/${f.id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:text-indigo-800" data-testid={`download-file-${f.id}`}><Download className="w-3.5 h-3.5" /></a>
                                    <button onClick={() => deleteFile(f.id)} className="text-xs text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-file-${f.id}`}><X className="w-3.5 h-3.5" /></button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="mt-2 flex items-center gap-2">
                              <label className="cursor-pointer text-xs text-slate-400 hover:text-indigo-600 flex items-center gap-1 transition-colors">
                                <Upload className="w-3.5 h-3.5" /><span>Joindre un fichier</span>
                                <input type="file" multiple className="hidden" onChange={(e) => handleFileUploadForExisting(inter.id, e)} data-testid={`upload-file-${inter.id}`} />
                              </label>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button variant="ghost" size="sm" onClick={() => openEditInteraction(inter)} data-testid={`edit-interaction-${inter.id}`}><Pencil className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => deleteInteraction(inter.id)} data-testid={`delete-interaction-${inter.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Communication Tab - WhatsApp / Email / SMS */}
          <TabsContent value="communication">
            <ClientCommunicationTab client={client} clientId={clientId} />
          </TabsContent>

          {/* Pieces jointes Tab */}
          <TabsContent value="pieces-jointes">
            <ClientAttachmentsTab clientId={clientId} />
          </TabsContent>

          {/* Gardiennage Tab */}
          <TabsContent value="gardiennage">
            <div className="space-y-4">
              <div className="flex items-center justify-end gap-2">
                <Button size="sm" onClick={openNewStorage} data-testid="add-storage-btn"><Plus className="mr-1 w-4 h-4" /> Nouveau gardiennage</Button>
                <Button size="sm" variant="outline" onClick={() => downloadPdf('gardiennage')} data-testid="pdf-gardiennage-btn"><Printer className="mr-1 w-4 h-4" /> PDF</Button>
              </div>

              {activeStorages.length === 0 && mountedStorages.length === 0 && returnedStorages.length === 0 ? (
                <Card className="p-8 text-center rounded-xl">
                  <Package className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm text-slate-500">Aucun gardiennage</p>
                  <Button size="sm" variant="outline" className="mt-3" onClick={openNewStorage} data-testid="add-first-storage-btn"><Plus className="mr-1 w-4 h-4" /> Ajouter un gardiennage</Button>
                </Card>
              ) : (
                <>
                  {/* Active storages - En stock */}
                  {activeStorages.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2"><CircleDot className="w-4 h-4 text-emerald-500" /> En stock ({activeStorages.length})</h3>
                      {activeStorages.map(s => (
                        <StorageCard key={s.id} storage={s} onEdit={() => openEditStorage(s)} onRotate={() => openRotateStorage(s)} onReturn={() => returnStorage(s.id)} onMount={() => mountStorage(s.id)} onDelete={() => deleteStorage(s.id)} formatDate={formatDate} />
                      ))}
                    </div>
                  )}
                  {/* Mounted storages - Monté sur véhicule */}
                  {mountedStorages.length > 0 && (
                    <div className="space-y-3 mt-6">
                      <h3 className="text-sm font-bold text-blue-700 flex items-center gap-2"><Car className="w-4 h-4 text-blue-500" /> Monte sur vehicule ({mountedStorages.length})</h3>
                      {mountedStorages.map(s => (
                        <StorageCard key={s.id} storage={s} mounted onUnmount={() => unmountStorage(s.id)} onReturn={() => returnStorage(s.id)} onDelete={() => deleteStorage(s.id)} formatDate={formatDate} />
                      ))}
                    </div>
                  )}
                  {/* Returned storages */}
                  {returnedStorages.length > 0 && (
                    <div className="space-y-3 mt-6">
                      <h3 className="text-sm font-bold text-slate-400 flex items-center gap-2">Rendus ({returnedStorages.length})</h3>
                      {returnedStorages.map(s => (
                        <StorageCard key={s.id} storage={s} returned onDelete={() => deleteStorage(s.id)} formatDate={formatDate} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </TabsContent>

          {/* Vehicles Tab */}
          <TabsContent value="vehicles">
            <div className="space-y-4">
              <div className="flex items-center justify-end gap-2">
                <Button size="sm" onClick={() => { setEditVehicle(null); setVForm({ ...EMPTY_VFORM }); setShowVehicleDialog(true); }} data-testid="add-vehicle-btn"><Plus className="mr-1 w-4 h-4" /> Ajouter</Button>
                <Button size="sm" variant="outline" onClick={() => downloadPdf('vehicles')} data-testid="pdf-vehicles-btn"><Printer className="mr-1 w-4 h-4" /> PDF</Button>
              </div>
              {vehicles.length === 0 ? (
                <Card className="p-8 text-center rounded-xl"><Car className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p className="text-sm text-slate-500">Aucun véhicule</p></Card>
              ) : (
                <div className="grid gap-3">
                  {vehicles.map((v) => (
                    <Card key={v.id} className="p-4 rounded-xl border border-slate-200 shadow-sm" data-testid={`vehicle-card-${v.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center"><Car className="w-5 h-5" /></div>
                          <div>
                            <p className="text-sm font-bold text-slate-900">{v.brand} {v.model}</p>
                            <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5 flex-wrap">
                              <span className="font-mono font-bold text-indigo-600">{v.plate}</span>
                              {v.color && <span>{v.color}</span>}
                              {v.body_type && <span>{v.body_type}</span>}
                              {v.mileage > 0 && <span>{v.mileage.toLocaleString()} km</span>}
                            </div>
                            {(v.displacement_cc > 0 || v.power_kw > 0 || v.first_registration) && (
                              <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5 flex-wrap">
                                {v.displacement_cc > 0 && <span>{v.displacement_cc} cm3</span>}
                                {v.power_kw > 0 && <span>{v.power_kw} kW</span>}
                                {v.first_registration && <span>MEC: {v.first_registration}</span>}
                                {v.chassis_number && <span className="font-mono">Chassis: {v.chassis_number}</span>}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditVehicle(v)} data-testid={`edit-vehicle-${v.id}`}><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => deleteVehicle(v.id)} data-testid={`delete-vehicle-${v.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Interventions Tab */}
          <TabsContent value="interventions">
            <div className="space-y-4">
              <div className="flex items-center justify-end">
                <Button size="sm" variant="outline" onClick={() => downloadPdf('interventions')} data-testid="pdf-interventions-btn"><Printer className="mr-1 w-4 h-4" /> PDF</Button>
              </div>
              {interventions.length === 0 ? (
                <Card className="p-8 text-center rounded-xl"><Wrench className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p className="text-sm text-slate-500">Aucune intervention</p></Card>
              ) : (
                <div className="grid gap-3">
                  {interventions.map((inter) => {
                    const st = STATUS_CONFIG[inter.status] || STATUS_CONFIG.open;
                    const h = Math.floor(inter.total_minutes / 60);
                    const m = inter.total_minutes % 60;
                    return (
                      <Card key={inter.id} className="p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/admin/work-orders/${inter.id}`)} data-testid={`intervention-${inter.id}`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-1.5 min-h-[50px] rounded-full mt-0.5 ${CATEGORY_COLORS[inter.category] || 'bg-slate-300'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-bold text-slate-900">{inter.vehicle_info}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.color}`}>{st.label}</span>
                              {inter.has_signature && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-500 mt-1 flex-wrap">
                              <span>{inter.date}</span>
                              {inter.category && <span className="capitalize">{inter.category}</span>}
                            </div>
                            {inter.description && <p className="text-xs text-slate-400 mt-1 truncate">{inter.description}</p>}
                            <div className="flex items-center gap-4 mt-2 flex-wrap">
                              {inter.mechanics.length > 0 && <div className="flex items-center gap-1 text-xs"><User className="w-3 h-3 text-slate-400" /><span className="text-slate-600 font-medium">{inter.mechanics.join(', ')}</span></div>}
                              {inter.total_minutes > 0 && <div className="flex items-center gap-1 text-xs"><Clock className="w-3 h-3 text-slate-400" /><span className="text-slate-600 font-mono font-bold">{h}h{String(m).padStart(2, '0')}</span></div>}
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Vehicle Dialog */}
        <Dialog open={showVehicleDialog} onOpenChange={(open) => { setShowVehicleDialog(open); if (!open) stopQrScan(); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editVehicle ? 'Modifier le véhicule' : 'Ajouter un véhicule'}</DialogTitle></DialogHeader>

            {/* Scan Carte Grise - Two options */}
            {!qrSession ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="border-2 border-dashed border-indigo-200 rounded-lg p-3 text-center bg-indigo-50/50 hover:border-indigo-400 transition-colors">
                  <input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" id="scan-carte-grise-input" onChange={handleScanCarteGrise} disabled={scanning} data-testid="scan-carte-grise-input" />
                  <label htmlFor="scan-carte-grise-input" className={`flex flex-col items-center gap-1.5 cursor-pointer ${scanning ? 'opacity-60 pointer-events-none' : ''}`}>
                    {scanning ? <Loader2 className="w-5 h-5 animate-spin text-indigo-600" /> : <Camera className="w-5 h-5 text-indigo-600" />}
                    <span className="text-xs font-medium text-indigo-700">{scanning ? 'Analyse...' : 'Photo directe'}</span>
                  </label>
                </div>
                <div
                  className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center bg-slate-50/50 hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors cursor-pointer"
                  onClick={startQrScan}
                  data-testid="qr-scan-btn"
                >
                  <QrCode className="w-5 h-5 text-slate-600 mx-auto" />
                  <span className="text-xs font-medium text-slate-700 mt-1.5 block">Scanner via QR</span>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-indigo-200 p-4 text-center space-y-3">
                <p className="text-sm font-medium text-slate-700">Scannez ce QR code avec votre téléphone</p>
                <img src={qrSession.qr_code} alt="QR Code" className="w-48 h-48 mx-auto" data-testid="qr-code-image" />
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                  <span className="text-xs text-slate-500">En attente de la photo...</span>
                </div>
                <Button variant="ghost" size="sm" onClick={stopQrScan} className="text-slate-400" data-testid="cancel-qr-btn">
                  <X className="w-4 h-4 mr-1" /> Annuler
                </Button>
              </div>
            )}

            <form onSubmit={handleVehicleSubmit} className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Informations principales</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Marque *</Label><Input value={vForm.brand} onChange={(e) => setVForm({ ...vForm, brand: e.target.value })} placeholder="Ex: BMW" required data-testid="vehicle-brand" /></div>
                <div className="space-y-2"><Label>Modele</Label><Input value={vForm.model} onChange={(e) => setVForm({ ...vForm, model: e.target.value })} placeholder="Ex: 320d" data-testid="vehicle-model" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Plaque *</Label><Input value={vForm.plate} onChange={(e) => setVForm({ ...vForm, plate: e.target.value.toUpperCase() })} placeholder="Ex: NE 83577" required data-testid="vehicle-plate" /></div>
                <div className="space-y-2"><Label>Couleur</Label><Input value={vForm.color} onChange={(e) => setVForm({ ...vForm, color: e.target.value })} placeholder="Ex: blanc" data-testid="vehicle-color" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Type / Carrosserie</Label><Input value={vForm.body_type} onChange={(e) => setVForm({ ...vForm, body_type: e.target.value })} placeholder="Ex: Limousine" data-testid="vehicle-body-type" /></div>
                <div className="space-y-2"><Label>Genre</Label><Input value={vForm.vehicle_genre} onChange={(e) => setVForm({ ...vForm, vehicle_genre: e.target.value })} placeholder="Ex: Voiture de tourisme" data-testid="vehicle-genre" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Kilometrage</Label><Input type="number" value={vForm.mileage} onChange={(e) => setVForm({ ...vForm, mileage: parseInt(e.target.value) || 0 })} data-testid="vehicle-mileage" /></div>
                <div className="space-y-2"><Label>N chassis</Label><Input value={vForm.chassis_number} onChange={(e) => setVForm({ ...vForm, chassis_number: e.target.value.toUpperCase() })} placeholder="Ex: JM YSN CP9..." data-testid="vehicle-chassis" /></div>
              </div>

              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2">Données techniques</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2"><Label>Cylindree (cm3)</Label><Input type="number" value={vForm.displacement_cc} onChange={(e) => setVForm({ ...vForm, displacement_cc: parseInt(e.target.value) || 0 })} data-testid="vehicle-displacement" /></div>
                <div className="space-y-2"><Label>Puissance (kW)</Label><Input type="number" step="0.01" value={vForm.power_kw} onChange={(e) => setVForm({ ...vForm, power_kw: parseFloat(e.target.value) || 0 })} data-testid="vehicle-power" /></div>
                <div className="space-y-2"><Label>Code emissions</Label><Input value={vForm.emission_code} onChange={(e) => setVForm({ ...vForm, emission_code: e.target.value })} placeholder="Ex: B02" data-testid="vehicle-emission" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2"><Label>Poids vide (kg)</Label><Input type="number" value={vForm.empty_weight_kg} onChange={(e) => setVForm({ ...vForm, empty_weight_kg: parseInt(e.target.value) || 0 })} data-testid="vehicle-empty-weight" /></div>
                <div className="space-y-2"><Label>Charge utile (kg)</Label><Input type="number" value={vForm.payload_kg} onChange={(e) => setVForm({ ...vForm, payload_kg: parseInt(e.target.value) || 0 })} data-testid="vehicle-payload" /></div>
                <div className="space-y-2"><Label>Poids total (kg)</Label><Input type="number" value={vForm.total_weight_kg} onChange={(e) => setVForm({ ...vForm, total_weight_kg: parseInt(e.target.value) || 0 })} data-testid="vehicle-total-weight" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Places (total)</Label><Input type="number" value={vForm.seats_total} onChange={(e) => setVForm({ ...vForm, seats_total: parseInt(e.target.value) || 0 })} data-testid="vehicle-seats-total" /></div>
                <div className="space-y-2"><Label>Places (avant)</Label><Input type="number" value={vForm.seats_front} onChange={(e) => setVForm({ ...vForm, seats_front: parseInt(e.target.value) || 0 })} data-testid="vehicle-seats-front" /></div>
              </div>

              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2">Administration</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>N matricule</Label><Input value={vForm.registration_number} onChange={(e) => setVForm({ ...vForm, registration_number: e.target.value })} placeholder="Ex: 614.798.865" data-testid="vehicle-reg-number" /></div>
                <div className="space-y-2"><Label>Reception par type</Label><Input value={vForm.type_approval} onChange={(e) => setVForm({ ...vForm, type_approval: e.target.value })} placeholder="Ex: 1MK3 73" data-testid="vehicle-type-approval" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>1ere mise en circulation</Label><Input value={vForm.first_registration} onChange={(e) => setVForm({ ...vForm, first_registration: e.target.value })} placeholder="Ex: 09.02.2000" data-testid="vehicle-first-reg" /></div>
                <div className="space-y-2"><Label>Dernière expertise</Label><Input value={vForm.last_inspection} onChange={(e) => setVForm({ ...vForm, last_inspection: e.target.value })} placeholder="Ex: 08.05.2015/NE" data-testid="vehicle-last-inspection" /></div>
              </div>

              <Button type="submit" className="w-full btn-primary" data-testid="submit-vehicle-btn">{editVehicle ? 'Enregistrer' : 'Ajouter le véhicule'}</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* CRM Interaction Dialog */}
        <Dialog open={showInteractionDialog} onOpenChange={setShowInteractionDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editInteraction ? 'Modifier le contact' : 'Nouveau contact client'}</DialogTitle></DialogHeader>
            <form onSubmit={handleInteractionSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Type de contact</Label>
                <Select value={iForm.interaction_type} onValueChange={(v) => setIForm({ ...iForm, interaction_type: v })}>
                  <SelectTrigger data-testid="interaction-type-select"><SelectValue placeholder="Sélectionnez un type" /></SelectTrigger>
                  <SelectContent>{interactionTypes.types.map(t => <SelectItem key={t} value={t}>{interactionTypes.labels[t]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes / Compte rendu</Label>
                <Textarea value={iForm.notes} onChange={(e) => setIForm({ ...iForm, notes: e.target.value })} rows={4} placeholder="Detaillez le contenu de l'echange..." data-testid="interaction-notes" />
              </div>
              <div className="space-y-3 bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <input type="checkbox" id="follow_up" checked={iForm.follow_up_needed} onChange={(e) => setIForm({ ...iForm, follow_up_needed: e.target.checked })} className="rounded border-slate-300" data-testid="follow-up-checkbox" />
                  <Label htmlFor="follow_up" className="cursor-pointer text-sm font-medium">Suivi necessaire</Label>
                </div>
                {iForm.follow_up_needed && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Date de rappel</Label><Input type="date" value={iForm.follow_up_date} onChange={(e) => setIForm({ ...iForm, follow_up_date: e.target.value })} data-testid="follow-up-date" /></div>
                    <div className="space-y-2"><Label>Action</Label><Input value={iForm.follow_up_action} onChange={(e) => setIForm({ ...iForm, follow_up_action: e.target.value })} placeholder="Rappeler, Envoyer devis..." data-testid="follow-up-action" /></div>
                  </div>
                )}
              </div>
              {!editInteraction && (
                <div className="space-y-2">
                  <Label>Pieces jointes</Label>
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-indigo-300 transition-colors">
                    <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} data-testid="file-input" />
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 mx-auto text-sm text-slate-500 hover:text-indigo-600 transition-colors" data-testid="select-files-btn"><Upload className="w-5 h-5" /><span>Cliquer pour joindre des fichiers (max 10 MB)</span></button>
                  </div>
                  {pendingFiles.length > 0 && (
                    <div className="space-y-1 mt-2">
                      {pendingFiles.map((f, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5">
                          <Paperclip className="w-3.5 h-3.5 text-slate-400" /><span className="text-xs text-slate-600 truncate flex-1">{f.name}</span><span className="text-xs text-slate-400">{formatFileSize(f.size)}</span>
                          <button type="button" onClick={() => removePendingFile(idx)} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <Button type="submit" className="w-full btn-primary" disabled={uploadingFiles} data-testid="submit-interaction-btn">{uploadingFiles ? 'Upload en cours...' : editInteraction ? 'Enregistrer' : 'Enregistrer le contact'}</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Gardiennage Dialog (Create/Edit) */}
        <Dialog open={showStorageDialog} onOpenChange={setShowStorageDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editStorage ? 'Modifier le gardiennage' : 'Nouveau gardiennage'}</DialogTitle></DialogHeader>
            <form onSubmit={handleStorageSubmit} className="space-y-4">
              <StorageFormFields sForm={sForm} setSForm={setSForm} vehicles={vehicles} />
              <Button type="submit" className="w-full btn-primary" data-testid="submit-storage-btn">{editStorage ? 'Enregistrer' : 'Creer le gardiennage'}</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Rotation Dialog */}
        <Dialog open={showRotateDialog} onOpenChange={setShowRotateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Rotation saisonniere</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-500 -mt-2">Mettre a jour les informations des pneus pour la nouvelle saison</p>
            <form onSubmit={handleRotateSubmit} className="space-y-4">
              <StorageFormFields sForm={sForm} setSForm={setSForm} vehicles={vehicles} hideVehicle />
              <Button type="submit" className="w-full btn-primary" data-testid="submit-rotate-btn">Effectuer la rotation</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

// Reusable storage form fields
const StorageFormFields = ({ sForm, setSForm, vehicles, hideVehicle }) => {
  const ACCESSORY_OPTIONS = [
    { value: 'chaines', label: 'Chaines a neige' },
    { value: 'galerie', label: 'Galerie de toit' },
    { value: 'barres_de_toit', label: 'Barres de toit' },
    { value: 'coffre_de_toit', label: 'Coffre de toit' },
    { value: 'enjoliveurs', label: 'Enjoliveurs' },
    { value: 'boulons_antivol', label: 'Boulons antivol' },
  ];
  const toggleAccessory = (val) => {
    const current = sForm.accessories || [];
    const updated = current.includes(val) ? current.filter(a => a !== val) : [...current, val];
    setSForm(f => ({ ...f, accessories: updated }));
  };
  return (
  <>
    {!hideVehicle && (
      <div className="space-y-2">
        <Label>Vehicule *</Label>
        <Select value={sForm.vehicle_id} onValueChange={(v) => setSForm(f => ({ ...f, vehicle_id: v }))}>
          <SelectTrigger data-testid="storage-vehicle-select"><SelectValue placeholder="Selectionnez un vehicule" /></SelectTrigger>
          <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} - {v.plate}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    )}
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-2">
        <Label>Type</Label>
        <Select value={sForm.tire_type} onValueChange={(v) => setSForm(f => ({ ...f, tire_type: v }))}>
          <SelectTrigger data-testid="storage-tire-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="roues_completes">Roues completes</SelectItem>
            <SelectItem value="pneus_seuls">Pneus seuls</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Jantes</Label>
        <Select value={sForm.rim_type} onValueChange={(v) => setSForm(f => ({ ...f, rim_type: v }))}>
          <SelectTrigger data-testid="storage-rim-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="alu">Alu</SelectItem>
            <SelectItem value="acier">Acier</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-2"><Label>Marque pneus</Label><Input value={sForm.tire_brand} onChange={(e) => setSForm(f => ({ ...f, tire_brand: e.target.value }))} placeholder="Ex: Michelin" data-testid="storage-tire-brand" /></div>
      <div className="space-y-2"><Label>Dimension</Label><Input value={sForm.tire_dimension} onChange={(e) => setSForm(f => ({ ...f, tire_dimension: e.target.value }))} placeholder="Ex: 225/45 R17" data-testid="storage-tire-dimension" /></div>
    </div>
    <div className="space-y-2">
      <Label>Etat des pneus (nombre par etat)</Label>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500" /><span className="text-xs text-slate-600">Bon</span></div>
          <Input type="number" min="0" max="8" value={sForm.qty_bon} onChange={(e) => setSForm(f => ({ ...f, qty_bon: parseInt(e.target.value) || 0 }))} data-testid="storage-qty-bon" />
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-amber-500" /><span className="text-xs text-slate-600">Moyen</span></div>
          <Input type="number" min="0" max="8" value={sForm.qty_moyen} onChange={(e) => setSForm(f => ({ ...f, qty_moyen: parseInt(e.target.value) || 0 }))} data-testid="storage-qty-moyen" />
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-red-500" /><span className="text-xs text-slate-600">A remplacer</span></div>
          <Input type="number" min="0" max="8" value={sForm.qty_a_remplacer} onChange={(e) => setSForm(f => ({ ...f, qty_a_remplacer: parseInt(e.target.value) || 0 }))} data-testid="storage-qty-a-remplacer" />
        </div>
      </div>
      <p className="text-xs text-slate-400">Total: {(sForm.qty_bon || 0) + (sForm.qty_moyen || 0) + (sForm.qty_a_remplacer || 0)} pneu(s)</p>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-2">
        <Label>Saison</Label>
        <Select value={sForm.season} onValueChange={(v) => setSForm(f => ({ ...f, season: v }))}>
          <SelectTrigger data-testid="storage-season"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="hiver">Hiver</SelectItem>
            <SelectItem value="ete">Ete</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2"><Label>Emplacement de stockage</Label><Input value={sForm.storage_location} onChange={(e) => setSForm(f => ({ ...f, storage_location: e.target.value }))} placeholder="Ex: Etagere A3, Rack B2" data-testid="storage-location" /></div>
    </div>
    <div className="space-y-2">
      <Label>Accessoires stockes</Label>
      <div className="grid grid-cols-2 gap-2">
        {ACCESSORY_OPTIONS.map(opt => (
          <label key={opt.value} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${(sForm.accessories || []).includes(opt.value) ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'}`} data-testid={`accessory-${opt.value}`}>
            <input type="checkbox" checked={(sForm.accessories || []).includes(opt.value)} onChange={() => toggleAccessory(opt.value)} className="rounded border-slate-300" />
            <span className="text-sm text-slate-700">{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-2"><Label>Date debut</Label><Input type="date" value={sForm.start_date} onChange={(e) => setSForm(f => ({ ...f, start_date: e.target.value }))} data-testid="storage-start-date" /></div>
      <div className="space-y-2"><Label>Date fin (6 mois)</Label><Input type="date" value={sForm.end_date} onChange={(e) => setSForm(f => ({ ...f, end_date: e.target.value }))} data-testid="storage-end-date" /></div>
    </div>
    <div className="space-y-2"><Label>Notes</Label><Textarea value={sForm.notes} onChange={(e) => setSForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Remarques..." data-testid="storage-notes" /></div>
  </>
  );
};

// Storage card component
const StorageCard = ({ storage: s, onEdit, onRotate, onReturn, onMount, onUnmount, onDelete, returned, mounted, formatDate }) => {
  const total = (s.qty_bon || 0) + (s.qty_moyen || 0) + (s.qty_a_remplacer || 0);
  const accessories = s.accessories || [];
  const ACCESSORY_LABELS = { chaines: 'Chaines', galerie: 'Galerie de toit', barres_de_toit: 'Barres de toit', coffre_de_toit: 'Coffre de toit', enjoliveurs: 'Enjoliveurs', boulons_antivol: 'Boulons antivol' };
  return (
  <Card className={`p-4 rounded-xl border shadow-sm ${returned ? 'border-slate-200 bg-slate-50 opacity-60' : mounted ? 'border-blue-200 bg-blue-50/30' : 'border-slate-200'}`} data-testid={`storage-card-${s.id}`}>
    <div className="flex items-start gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${mounted ? 'bg-blue-100 text-blue-700' : s.season === 'hiver' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}`}>
        {mounted ? <Car className="w-5 h-5" /> : <Package className="w-5 h-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-slate-900">{s.vehicle_info}</span>
          <Badge className={`text-xs ${s.season === 'hiver' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}`}>{s.season === 'hiver' ? 'Hiver' : 'Ete'}</Badge>
          {mounted && <Badge className="text-xs bg-blue-100 text-blue-700" data-testid="badge-mounted">Monte sur vehicule</Badge>}
          <span className="text-xs font-medium text-slate-600">{total} pneu(s)</span>
          {returned && <Badge className="text-xs bg-slate-200 text-slate-600">Rendu</Badge>}
        </div>
        {/* Qty condition badges */}
        <div className="flex items-center gap-2 mt-1.5">
          {(s.qty_bon || 0) > 0 && <Badge className="text-xs bg-emerald-100 text-emerald-700">{s.qty_bon} Bon</Badge>}
          {(s.qty_moyen || 0) > 0 && <Badge className="text-xs bg-amber-100 text-amber-700">{s.qty_moyen} Moyen</Badge>}
          {(s.qty_a_remplacer || 0) > 0 && <Badge className="text-xs bg-red-100 text-red-700">{s.qty_a_remplacer} A remplacer</Badge>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 mt-2 text-xs text-slate-600">
          <span><span className="text-slate-400">Type:</span> {s.tire_type === 'roues_completes' ? 'Roues completes' : 'Pneus seuls'}</span>
          <span><span className="text-slate-400">Jantes:</span> {s.rim_type === 'alu' ? 'Alu' : 'Acier'}</span>
          <span><span className="text-slate-400">Marque:</span> {s.tire_brand || '-'}</span>
          <span><span className="text-slate-400">Dimension:</span> {s.tire_dimension || '-'}</span>
        </div>
        <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
          {s.storage_location && <span><span className="text-slate-400">Emplacement:</span> {s.storage_location}</span>}
          {s.start_date && <span>{formatDate(s.start_date)} - {formatDate(s.end_date)}</span>}
        </div>
        {s.notes && <p className="text-xs text-slate-400 mt-1">{s.notes}</p>}
        {/* Accessories */}
        {accessories.length > 0 && (
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <span className="text-xs text-slate-400">Accessoires:</span>
            {accessories.map((a, i) => (
              <Badge key={i} className="text-[10px] bg-indigo-50 text-indigo-600">{ACCESSORY_LABELS[a] || a}</Badge>
            ))}
          </div>
        )}
        {/* History */}
        {s.history && s.history.length > 1 && (
          <details className="mt-2">
            <summary className="text-xs text-indigo-600 cursor-pointer hover:text-indigo-800">Historique ({s.history.length} entrees)</summary>
            <div className="mt-1 space-y-1 pl-2 border-l-2 border-slate-200">
              {s.history.slice().reverse().map((h, i) => (
                <div key={i} className="text-xs text-slate-500">
                  <span className="font-medium">{formatDate(h.date)}</span>
                  <span className="mx-1">-</span>
                  <span className="capitalize">{h.action === 'creation' ? 'Creation' : h.action === 'rotation' ? 'Rotation' : h.action === 'montage' ? 'Montage' : h.action === 'demontage' ? 'Demontage' : 'Rendu'}</span>
                  <span className="mx-1">-</span>
                  <span>{h.season === 'hiver' ? 'Hiver' : 'Ete'}</span>
                  <span className="mx-1">-</span>
                  <span>{(h.qty_bon||0)} bon, {(h.qty_moyen||0)} moyen, {(h.qty_a_remplacer||0)} a rempl.</span>
                  {h.notes && <span className="text-slate-400 ml-1">({h.notes})</span>}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
      {/* Action buttons for en_stock */}
      {!returned && !mounted && (
        <div className="flex flex-col items-center gap-1 shrink-0">
          <Button variant="ghost" size="sm" onClick={onMount} title="Monter sur le vehicule" data-testid={`mount-storage-${s.id}`}><Car className="w-4 h-4 text-blue-500" /></Button>
          <Button variant="ghost" size="sm" onClick={onRotate} title="Rotation saisonniere" data-testid={`rotate-storage-${s.id}`}><RotateCcw className="w-4 h-4 text-indigo-500" /></Button>
          <Button variant="ghost" size="sm" onClick={onEdit} data-testid={`edit-storage-${s.id}`}><Pencil className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" className="text-amber-500 hover:text-amber-700" onClick={onReturn} title="Rendre au client" data-testid={`return-storage-${s.id}`}><X className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={onDelete} data-testid={`delete-storage-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      )}
      {/* Action buttons for monte_sur_vehicule */}
      {mounted && (
        <div className="flex flex-col items-center gap-1 shrink-0">
          <Button variant="ghost" size="sm" onClick={onUnmount} title="Remettre en stock" data-testid={`unmount-storage-${s.id}`}><Package className="w-4 h-4 text-emerald-500" /></Button>
          <Button variant="ghost" size="sm" className="text-amber-500 hover:text-amber-700" onClick={onReturn} title="Rendre au client" data-testid={`return-storage-${s.id}`}><X className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={onDelete} data-testid={`delete-storage-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      )}
      {/* Action buttons for rendu */}
      {returned && (
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={onDelete} data-testid={`delete-storage-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      )}
    </div>
  </Card>
  );
};

export default ClientDetailPage;
